import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { FenceService } from 'src/app/services/configurators-2d/applications/fence.service';
import { Board } from 'src/app/models/configurators-2d/applications/fence/board';
import { PostType } from 'src/app/models/configurators-2d/applications/fence/post-type';
import { Decoration } from 'src/app/models/configurators-2d/applications/fence/decoration';
import { Door } from 'src/app/models/configurators-2d/applications/fence/door';
import { Side } from 'src/app/models/configurators-2d/applications/fence/side';
import { Configuration } from 'src/app/models/configurators-2d/applications/fence/configuration';
import {
  checkPanelCount,
  getMaxHeight,
  getSideLengthForDecoration,
  rotateImg,
  setPanelCount,
} from '../../../../../services/helpers/fence-helper';
import { FencePreviewComponent } from '../../preview/preview.component';
import { Color } from '../../../../../models/configurators-2d/applications/fence/color';
import { cloneDeep } from '../../../../../services/helpers/utils';
import { MODAL, ModalService } from '../../../../../services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { concat, forkJoin, from, map, Observable, of, switchMap, tap, toArray } from 'rxjs';
import { Installation } from '../../../../../models/configurators-2d/applications/fence/installations/installation';
import { ConfigurationBoard } from '../../../../../models/configurators-2d/applications/fence/configuration-board';
import { InstallationStateService } from '../../services/installation.state.service';
import { BoardStateService } from '../../services/board.state.service';
import { ColorStateService } from '../../services/color.state.service';
import { PostTypeStateService } from '../../services/post-type.state.service';
import { DecorationStateService } from '../../services/decoration.state.service';
import { DoorStateService } from '../../services/door.state.service';
import { environment } from 'src/environments/environment';
import { InstallationTypeEnum } from 'src/app/enum/fence/installation-type.enum';
import { DataLoaderService } from '../../services/data-loader.service';
import { InstallationMixing } from '../../../../../models/configurators-2d/applications/fence/installations/installation-mixing';
import {
  ConfigurationInstallation
} from '../../../../../models/configurators-2d/applications/fence/configuration-installation';
import { InstallationMixingStateService } from '../../services/installation-mixing.state.service';
import { FencePanelCalculatorService } from '../../services/fence-panel-calculator.service';

@Injectable({ providedIn: 'root' })
export class MaterialsFacadeService {
  /**
   * Configuration
   */
  configuration: WritableSignal<Configuration | undefined> = signal(undefined);

  /**
   * Si la configuration comporte des portillons
   * @returns boolean
   */
  hasDoor: Signal<boolean> = computed(() => {
    return this.configuration().sides.some(side => side.sections.some(section => section.hasDoor));
  });

  /**
   * Checke si toutes les décorations choisies ne sont pas désactivées
   */
  isDecorationsValid: Signal<boolean> = computed(() => {
    const configuration = this.configuration();

    return (
      !configuration.horizontalDecorations.some(hd => hd.quantity && hd.isDisabled) &&
      configuration.sides.every(s => !s.verticalDecorations.some(vd => vd.quantity && vd.isDisabled))
    );
  });

  /**
   * Checke si toutes les décorations choisies ne sont pas désactivées
   */
  isPanelsValid: Signal<boolean> = computed(() => {
    const configuration = this.configuration();

    return (
      !configuration.horizontalPanels.some(hp => hp.quantity && hp.isDisabled) &&
      configuration.sides.every(s => !s.verticalPanels.some(vp => vp.quantity && vp.isDisabled))
    );
  });

  /**
   * Checke si toutes les lames décoratives choisies ne sont pas désactivées
   */
  isDecorativeBoardsValid: Signal<boolean> = computed(() => {
    const configuration = this.configuration();

    return (
      !configuration.horizontalDecorativeBoards.some(hd => hd.quantity && hd.isDisabled) &&
      configuration.sides.every(s => !s.verticalDecorativeBoards.some(vd => vd.quantity && vd.isDisabled))
    );
  });

  /**
   * Check si tous les portillons choisis ne sont pas désactivés
   */
  isDoorsValid: Signal<boolean> = computed(() => {
    const configuration = this.configuration();
    return configuration.doors.every(d => !d.isDisabled);
  });

  /**
   * Permet de récupérer le message de déréférencement, suivant customer/vendor
   */
  disableMessage: Signal<string> = computed(() => {
    return environment.vendor
      ? this.translateService.instant('fence.materials.disable-message-vendor')
      : this.translateService.instant('fence.materials.disable-message-customer');
  });

  private readonly fenceService = inject(FenceService);
  private readonly modalService = inject(ModalService);
  private readonly translateService = inject(TranslateService);
  private readonly installationStateService = inject(InstallationStateService);
  private readonly installationMixingStateService = inject(InstallationMixingStateService);
  private readonly boardStateService = inject(BoardStateService);
  private readonly colorStateService = inject(ColorStateService);
  private readonly postTypeStateService = inject(PostTypeStateService);
  private readonly decorationStateService = inject(DecorationStateService);
  private readonly doorStateService = inject(DoorStateService);
  private readonly dataLoaderService = inject(DataLoaderService);
  private readonly fencePanelCalculatorService = inject(FencePanelCalculatorService);

  private fencePreview: FencePreviewComponent;

  /**
   * Initialise le service et la configuration (choix automatique de la première pose si aucune n'est sélectionnée)
   * @param configuration
   * @param fencePreview
   */
  init(
    configuration: Configuration,
    fencePreview: FencePreviewComponent
  ): Observable<void | (Board[] | Color[] | PostType[] | Decoration[] | Door[])[]> {
    // initialise les tableaux vident
    if (!configuration.horizontalPanels) {
      configuration.horizontalPanels = [];
    }

    if (!configuration.horizontalDecorations) {
      configuration.horizontalDecorations = [];
    }

    if (!configuration.horizontalDecorativeBoards) {
      configuration.horizontalDecorativeBoards = [];
    }

    if (!configuration.doors) {
      configuration.doors = [];
    }

    // force la MAJ de la configuration pour déclencher le calcul des computeds
    this.configuration.set(undefined);
    this.configuration.set(configuration);
    this.fencePreview = fencePreview;

    // Récupération des poses
    return forkJoin([this.installationStateService.setList(), this.installationMixingStateService.setList()]).pipe(
      switchMap(() => {
        // si c'est le chargement d'une configuration, on sélectionne les élements de la configuration
        if (this.configuration().configurationInstallations.length > 0) {
          return this.dataLoaderService.initData().pipe(switchMap(() => from(this.fencePreview.constructPreview())));
        }

        // si c'est une nouvelle configuration, on sélectionne la première pose
        return this.selectInstallation(this.installationStateService.installations()[0]);
      })
    );
  }

  /**
   * Sélection d'une pose
   * @param installation
   */
  selectInstallation(installation: Installation): Observable<void> {
    return of(undefined).pipe(
      switchMap(() => {
        if (
          this.configuration().configurationInstallations.length === 1 &&
          this.configuration().configurationInstallations.some(i => i.installationId === installation.id)
        ) {
          return of(undefined);
        }

        // on reset les portillons, panneaux, décorations et lames décoratives sélectionnés lorsqu'on change de pose
        this.resetConfigurationDecorativeBoards();
        this.resetConfigurationDecorations();
        this.resetConfigurationPanels();
        this.resetConfigurationDoors();

        const configuration = this.configuration();

        configuration.configurationInstallations = [
          {
            installationId: installation.id,
            order: 0,
            boardNumber: null,
            configurationBoards: [],
          },
        ];

        configuration.isVertical = installation.installationType === InstallationTypeEnum.VerticalInterlocking;

        this.updateConfiguration(configuration);

        // recharge les lames disponibles
        return this.boardStateService.setList(configuration).pipe(
          switchMap(() => {
            // On sélectionne la première lame disponible s'il n'y en a pas de sélectionnée
            const boards = this.boardStateService.getBoardByInstallation(installation.id);
            return this.selectBoard(configuration.configurationInstallations[0], boards[0]);
          })
        );
      })
    );
  }

  /**
   * Fonction de sélection d'une lame
   * @param configurationInstallation
   * @param board lame
   */
  selectBoard(configurationInstallation: ConfigurationInstallation, board: Board): Observable<void> {
    return of(undefined).pipe(
      switchMap(() => {
        const configuration = this.configuration();

        configurationInstallation.configurationBoards = [new ConfigurationBoard(0, board.id)];

        this.updateConfiguration(configuration);

        this.colorStateService.setList(configuration);

        this.setPanelCount();

        const colors = this.colorStateService.colorsByInstallations().get(configurationInstallation.installationId);
        return this.selectColor(configurationInstallation, colors[0]);
      })
    );
  }

  /**
   * Sélection de la lame
   * @param configurationInstallation
   * @param color
   */
  selectColor(configurationInstallation: ConfigurationInstallation, color: Color): Observable<void> {
    return of(undefined).pipe(
      switchMap(() => {
        const configuration = this.configuration();

        // type de lame associé à la lame
        const board = this.boardStateService
          .boardByInstallations()
          .get(configurationInstallation.installationId)
          .find(b => b.colors.some(c => c.id === color.id));

        if (color.primaryId && color.secondaryId) {
          // Forcément qu'une installation pour l'instant, on ne gère pas le mixage des poses
          configurationInstallation.configurationBoards = [
            new ConfigurationBoard(
              1,
              board.id,
              this.colorStateService.getByInstallationIdAndId(
                configurationInstallation.installationId,
                color.secondaryId
              ),
              1
            ),
            new ConfigurationBoard(
              0,
              board.id,
              this.colorStateService.getByInstallationIdAndId(configurationInstallation.installationId, color.primaryId)
            ),
          ];
        } else {
          // Forcément qu'une installation pour l'instant, on ne gère pas le mixage des poses
          configurationInstallation.configurationBoards = [new ConfigurationBoard(0, board.id, color)];
        }

        this.setPanelCount();

        this.updateConfiguration(configuration);

        return this.postTypeStateService.setList(configuration).pipe(
          switchMap(() => {
            const postTypes = this.postTypeStateService.postTypes();

            if (postTypes.length === 0) {
              return of(undefined);
            }

            return this.selectPostType(postTypes[0]);
          })
        );
      })
    );
  }

  /**
   * Fonction de sélection d'un poteau
   * @param postType objet type de poteau
   */
  selectPostType(postType: PostType): Observable<void> {
    return of(undefined).pipe(
      switchMap(() => {
        const configuration = this.configuration();
        let isNewPostType = !configuration.postType || !postType || configuration.postType.id != postType.id;

        configuration.postType = postType;

        // si on change le poteau, on reset les décors, panneaux et portillons
        if (isNewPostType) {
          this.resetConfigurationDecorativeBoards();
          this.resetConfigurationDecorations();
          this.resetConfigurationPanels();
          this.resetConfigurationDoors();
        }

        this.setPanelCount();

        this.updateConfiguration(configuration);

        if (!postType.acceptAngle) {
          this.modalService.display(MODAL.MESSAGE, {
            titleRef: 'popins.fence.title',
            contentRef: 'popins.fence.tooltip-thin-post',
            primary: true,
            buttons: [
              {
                labelRef: 'generic.ok',
                action: () => {
                  this.modalService.close();
                },
              },
            ],
          });
        }

        const requests: Observable<Decoration[] | Door[]>[] = [this.decorationStateService.setList(configuration)];

        if (configuration.sides.some(s => s.sections.some(section => section.hasDoor))) {
          requests.push(this.doorStateService.setList(configuration));
        }

        // Récupération des décorations, panneaux et portillons
        return forkJoin(requests).pipe(
          // Puis MAJ de la préview
          switchMap(() => from(this.fencePreview.constructPreview()))
        );
      })
    );
  }

  /**
   * Fonction permettant de vérifier que les lames décoratives, décorations et les panneaux de la configuration ont été complétés
   * Le vendeur peut passer à l'étape suivante
   * @returns boolean
   */
  checkDecorativeElementsCompleted(configuration: Configuration): boolean {
    // On laisse la possibilité au vendeur de continuer normalement
    if (environment.vendor) {
      return true;
    }

    //Vérification des lames décoratives
    let isDecorativeBoardsCompleted = this.isDecorationsCompleted(configuration.horizontalDecorativeBoards);

    if (configuration.sides.some(s => s.verticalDecorativeBoards != null)) {
      for (let i = 0; i < configuration.sides.length; i++) {
        // Vérifier que ce côté spécifique a des verticalDecorativeBoards
        isDecorativeBoardsCompleted &&= this.isDecorationsCompleted(configuration.sides[i].verticalDecorativeBoards);
      }
    }

    //Vérification des décors
    let isDecorationsCompleted = this.isDecorationsCompleted(configuration.horizontalDecorations);

    if (configuration.sides.some(s => s.verticalDecorations != null)) {
      for (let i = 0; i < configuration.sides.length; i++) {
        // Vérifier que ce côté spécifique a des verticalDecorations
        isDecorationsCompleted &&= this.isDecorationsCompleted(configuration.sides[i].verticalDecorations);
      }
    }

    //Vérification des panneaux
    let isPanelsCompleted = this.isDecorationsCompleted(configuration.horizontalPanels);

    if (configuration.sides.some(s => s.verticalPanels != null)) {
      for (let i = 0; i < configuration.sides.length; i++) {
        // Vérifier que ce côté spécifique a des verticalPanels
        isPanelsCompleted &&= this.isDecorationsCompleted(configuration.sides[i].verticalPanels);
      }
    }

    return isDecorativeBoardsCompleted && isDecorationsCompleted && isPanelsCompleted;
  }

  /**
   * Fonction permettant de supprimer une décoration si sa quantité passe à 0
   * @param decorations
   * @param decoration
   */
  removeDecorationIfEmpty(decorations: Decoration[], decoration: Decoration) {
    if (decoration?.quantity === 0) {
      const index = decorations.findIndex(d => d.id === decoration.id);

      if (index > -1) {
        decorations.splice(index, 1);
      }
    }
  }

  /**
   * Fonction de tri permettant de changer l'ordre des décorations
   */
  sortDecorationsFn = (a: Decoration, b: Decoration) => {
    return a.displayOrder - b.displayOrder;
  };

  /**
   * Fonction de sélection d'une lame décorative
   * @param type horizontal ou vertical
   * @param decorativeBoard objet lame décorative
   */
  public addDecorativeBoard(type: 'horizontal' | 'vertical', decorativeBoard: Decoration): Observable<void> {
    const configuration = this.configuration();

    if (!configuration) {
      return of(undefined);
    }

    if (decorativeBoard.isDisabled) {
      if (type === 'horizontal') {
        // Supprimer de horizontalDecorativeBoards
        if (configuration.horizontalDecorativeBoards?.some(d => d.id === decorativeBoard.id)) {
          configuration.horizontalDecorativeBoards.splice(
            configuration.horizontalDecorativeBoards.findIndex(d => d.id === decorativeBoard.id),
            1
          );
        }
      } else if (type === 'vertical') {
        // Supprimer de tous les côtés (verticalDecorativeBoards)
        configuration.sides.forEach(side => {
          if (side.verticalDecorativeBoards?.some(d => d.id === decorativeBoard.id)) {
            const index = side.verticalDecorativeBoards.findIndex(d => d.id === decorativeBoard.id);
            if (index >= 0) {
              side.verticalDecorativeBoards.splice(index, 1);
            }
          }
        });
      }

      this.updateConfiguration(configuration);
      return from(this.fencePreview.constructPreview());
    }

    const cloneDecoration = cloneDeep(decorativeBoard);
    return from(rotateImg(this.fenceService.imgPath(cloneDecoration.path), 270)).pipe(
      switchMap((res: string) => {
        cloneDecoration.verticalPath = res;
        switch (type) {
          case 'horizontal':
            if (configuration.horizontalDecorativeBoards == undefined) {
              configuration.horizontalDecorativeBoards = [];
            }

            if (configuration.horizontalDecorativeBoards.some(d => d.id === cloneDecoration.id)) {
              let indexDecoration = configuration.horizontalDecorativeBoards.findIndex(
                d => d.id === cloneDecoration.id
              );
              configuration.horizontalDecorativeBoards.splice(indexDecoration, 1);
            } else {
              configuration.horizontalDecorativeBoards.unshift(cloneDecoration);
              configuration.horizontalDecorativeBoards[0].quantity = 1;
              configuration.horizontalDecorativeBoards[0].isHorizontal = true;
            }
            break;
          case 'vertical':
            for (let i = 0; i < configuration.sides.length; i++) {
              const cloneDecorationForSide = cloneDeep(decorativeBoard);
              cloneDecorationForSide.verticalPath = res;

              if (configuration.sides[i].verticalDecorativeBoards == undefined) {
                configuration.sides[i].verticalDecorativeBoards = [];
              }

              if (configuration.sides[i].verticalDecorativeBoards.some(d => d.id === cloneDecorationForSide.id)) {
                let indexVertical = configuration.sides[i].verticalDecorativeBoards.findIndex(
                  d => d.id === cloneDecorationForSide.id
                );
                configuration.sides[i].verticalDecorativeBoards.splice(indexVertical, 1);
              } else {
                configuration.sides[i].verticalDecorativeBoards.unshift(cloneDecorationForSide);
                configuration.sides[i].verticalDecorativeBoards[0].isVertical = true;
                configuration.sides[i].verticalDecorativeBoards[0].quantity = 0;
              }
            }
            break;
        }

        this.setPanelCount();
        this.updateConfiguration(configuration);
        this.checkPanelError();
        return from(this.fencePreview.constructPreview());
      })
    );
  }

  /**
   * Fonction de sélection d'un décor
   * @param type horizontal ou vertical
   * @param decoration objet decor
   */
  addDecoration(type: 'horizontal' | 'vertical', decoration: Decoration): Observable<void> {
    const configuration = this.configuration();

    if (!configuration) {
      return of(undefined);
    }

    if (decoration.isDisabled) {
      if (type === 'horizontal') {
        // Supprimer de horizontalDecorations
        if (configuration.horizontalDecorations?.some(d => d.id === decoration.id)) {
          configuration.horizontalDecorations.splice(
            configuration.horizontalDecorations.findIndex(d => d.id === decoration.id),
            1
          );
        }
      } else if (type === 'vertical') {
        // Supprimer de tous les côtés (verticalDecorations)
        configuration.sides.forEach(side => {
          if (side.verticalDecorations?.some(d => d.id === decoration.id)) {
            const index = side.verticalDecorations.findIndex(d => d.id === decoration.id);
            if (index >= 0) {
              side.verticalDecorations.splice(index, 1);
            }
          }
        });
      }

      this.updateConfiguration(configuration);
      return from(this.fencePreview.constructPreview());
    }

    const cloneDecoration = cloneDeep(decoration);
    return from(rotateImg(this.fenceService.imgPath(cloneDecoration.path), 270)).pipe(
      switchMap((res: string) => {
        cloneDecoration.verticalPath = res;
        switch (type) {
          case 'horizontal':
            if (configuration.horizontalDecorations == undefined) {
              configuration.horizontalDecorations = [];
            }

            if (configuration.horizontalDecorations.some(d => d.id === cloneDecoration.id)) {
              let indexDecoration = configuration.horizontalDecorations.findIndex(d => d.id === cloneDecoration.id);
              configuration.horizontalDecorations.splice(indexDecoration, 1);
            } else {
              configuration.horizontalDecorations.unshift(cloneDecoration);
              configuration.horizontalDecorations[0].quantity = 1;
              configuration.horizontalDecorations[0].isHorizontal = true;
            }
            break;
          case 'vertical':
            for (let i = 0; i < configuration.sides.length; i++) {
              const cloneDecorationForSide = cloneDeep(decoration);
              cloneDecorationForSide.verticalPath = res;

              if (configuration.sides[i].verticalDecorations == undefined) {
                configuration.sides[i].verticalDecorations = [];
              }

              if (configuration.sides[i].verticalDecorations.some(d => d.id === cloneDecorationForSide.id)) {
                let indexVertical = configuration.sides[i].verticalDecorations.findIndex(
                  d => d.id === cloneDecorationForSide.id
                );
                configuration.sides[i].verticalDecorations.splice(indexVertical, 1);
              } else {
                configuration.sides[i].verticalDecorations.unshift(cloneDecorationForSide);
                configuration.sides[i].verticalDecorations[0].isVertical = true;
                configuration.sides[i].verticalDecorations[0].quantity = 0;
              }
            }
            break;
        }

        this.setPanelCount();
        this.updateConfiguration(configuration);
        this.checkPanelError();
        return from(this.fencePreview.constructPreview());
      })
    );
  }

  /**
   * Fonction de suppression de toutes les lames décoratives
   */
  public deleteAllDecorativeBoards(): Observable<void> {
    return concat(
      this.resetDecors(this.configuration().horizontalDecorativeBoards),
      ...this.configuration().sides.map(s => this.resetDecors(s.verticalDecorativeBoards))
    ).pipe(
      toArray(), // Collecte tous les résultats
      map(() => undefined) // Retourne void
    );
  }

  /**
   * Fonction de suppression de tous les décors
   */
  deleteAllDecorations(): Observable<void> {
    return concat(
      this.resetDecors(this.configuration().horizontalDecorations),
      ...this.configuration().sides.map(s => this.resetDecors(s.verticalDecorations))
    ).pipe(
      toArray(), // Collecte tous les résultats
      map(() => undefined) // Retourne void
    );
  }

  /**
   * Fonction de sélection d'un panneau
   * @param type horizontal ou vertical
   * @param panel objet panneau
   */
  addPanel(type: string, panel: Decoration): Observable<void> {
    const configuration = this.configuration();
    if (!configuration) {
      return of(undefined);
    }

    // Gestion des panneaux désactivés
    if (panel.isDisabled) {
      if (type === 'horizontal') {
        // Supprimer de horizontalPanels
        if (configuration.horizontalPanels?.some(d => d.id === panel.id)) {
          configuration.horizontalPanels.splice(
            configuration.horizontalPanels.findIndex(d => d.id === panel.id),
            1
          );
        }
      } else if (type === 'vertical') {
        // Supprimer de tous les côtés (verticalPanels)
        configuration.sides.forEach(side => {
          if (side.verticalPanels?.some(d => d.id === panel.id)) {
            const index = side.verticalPanels.findIndex(d => d.id === panel.id);
            if (index >= 0) {
              side.verticalPanels.splice(index, 1);
            }
          }
        });
      }

      this.updateConfiguration(configuration);
      return from(this.fencePreview.constructPreview());
    }

    const clonePanel = cloneDeep(panel);
    return from(rotateImg(this.fenceService.imgPath(clonePanel.path), 270)).pipe(
      switchMap((res: string) => {
        clonePanel.verticalPath = res;
        switch (type) {
          case 'horizontal':
            if (configuration.horizontalPanels == undefined) {
              configuration.horizontalPanels = [];
            }

            if (configuration.horizontalPanels.some(d => d.id === clonePanel.id)) {
              let indexPanel = configuration.horizontalPanels.findIndex(d => d.id === clonePanel.id);
              configuration.horizontalPanels.splice(indexPanel, 1);
            } else {
              configuration.horizontalPanels.unshift(clonePanel);
              configuration.horizontalPanels[0].quantity = 1;
              configuration.horizontalPanels[0].isHorizontal = true;
            }
            break;
          case 'vertical':
            for (let i = 0; i < configuration.sides.length; i++) {
              const clonePanelForSide = cloneDeep(panel);
              clonePanelForSide.verticalPath = res;
              if (configuration.sides[i].verticalPanels == undefined) {
                configuration.sides[i].verticalPanels = [];
              }

              if (configuration.sides[i].verticalPanels.some(d => d.id === clonePanelForSide.id)) {
                let indexVertical = configuration.sides[i].verticalPanels.findIndex(d => d.id === clonePanelForSide.id);
                configuration.sides[i].verticalPanels.splice(indexVertical, 1);
              } else {
                configuration.sides[i].verticalPanels.unshift(clonePanelForSide);
                configuration.sides[i].verticalPanels[0].isVertical = true;
                configuration.sides[i].verticalPanels[0].quantity = 0;
              }
            }
            break;
        }
        this.setPanelCount();
        this.updateConfiguration(configuration);
        this.checkPanelError();
        return from(this.fencePreview.constructPreview());
      })
    );
  }

  /**
   * Fonction de détermination si le champ est coché ou non
   * @param type horizontal ou vertical
   * @param id id du panneau
   * @returns boolean
   */
  isPanelChecked(type: 'horizontal' | 'vertical', id: number): boolean {
    const configuration = this.configuration();

    if (!configuration) {
      return false;
    }

    switch (type) {
      case 'horizontal':
        if (configuration.horizontalPanels !== undefined) {
          if (configuration.horizontalPanels.some(d => d.id === id && d.quantity > 0)) {
            return true;
          }
        }
        return false;
      case 'vertical':
        return configuration.sides.some(s => s.verticalPanels.some(d => d.id === id));
      default:
        return false;
    }
  }

  /**
   * Fonction de changement de la quantité des décors
   * @param type horizontal ou vertical
   * @param panel
   * @param sideId
   * @param event l'événement de changement
   */
  changePanelQuantity(
    type: 'horizontal' | 'vertical',
    panel: Decoration,
    sideId: string,
    event: Event
  ): Observable<void> {
    const el = event.target as HTMLInputElement;
    const quantity = parseInt(el.value);

    return of(undefined).pipe(
      tap(() => {
        const configuration = this.configuration();
        if (!configuration) {
          return;
        }

        switch (type) {
          case 'horizontal':
            let indexPanel = configuration.horizontalPanels.findIndex(d => d.id === panel.id);
            let horizontalPanel = configuration.horizontalPanels[indexPanel];
            horizontalPanel.quantity = quantity;
            this.removeDecorationIfEmpty(configuration.horizontalPanels, horizontalPanel);
            break;
          case 'vertical':
            let indexSide = configuration.sides.findIndex(s => s.id === sideId);
            let indexVertPanel = configuration.sides[indexSide].verticalPanels.findIndex(d => d.id === panel.id);
            let verticalPanel = configuration.sides[indexSide].verticalPanels[indexVertPanel];
            verticalPanel.quantity = quantity;
            this.removeDecorationIfEmpty(configuration.sides[indexSide].verticalPanels, verticalPanel);
            break;
        }

        this.setPanelCount();
        this.updateConfiguration(configuration);
        this.checkPanelError();
      }),
      switchMap(() => from(this.fencePreview.constructPreview()))
    );
  }

  /**
   * Fonction de changement de la quantité des lames décoratives
   * @param type horizontal ou vertical
   * @param decorativeBoard
   * @param sideId id du côté s'il existe
   * @param event l'événement de changement
   */
  public changeDecorativeBoardQuantity(
    type: 'horizontal' | 'vertical',
    decorativeBoard: Decoration,
    sideId: string,
    event: Event
  ): Observable<void> {
    const el = event.target as HTMLInputElement;
    const quantity = parseInt(el.value);

    return of(undefined).pipe(
      tap(() => {
        const configuration = this.configuration();
        if (!configuration) {
          return;
        }

        switch (type) {
          case 'horizontal':
            let indexDecorativeBoard = configuration.horizontalDecorativeBoards.findIndex(
              d => d.id === decorativeBoard.id
            );
            let horizontalDecorativeBoard = configuration.horizontalDecorativeBoards[indexDecorativeBoard];
            horizontalDecorativeBoard.quantity = quantity;
            this.removeDecorationIfEmpty(configuration.horizontalDecorativeBoards, horizontalDecorativeBoard);
            break;
          case 'vertical':
            let indexSide = configuration.sides.findIndex(s => s.id === sideId);
            let indexVertDecoration = configuration.sides[indexSide].verticalDecorativeBoards.findIndex(
              d => d.id === decorativeBoard.id
            );
            let verticalDecorativeBoard = configuration.sides[indexSide].verticalDecorativeBoards[indexVertDecoration];
            verticalDecorativeBoard.quantity = quantity;
            this.removeDecorationIfEmpty(
              configuration.sides[indexSide].verticalDecorativeBoards,
              verticalDecorativeBoard
            );
            break;
        }

        this.setPanelCount();
        this.updateConfiguration(configuration);
        this.checkPanelError();
      }),
      switchMap(() => from(this.fencePreview.constructPreview()))
    );
  }

  /**
   * Fonction de changement de la quantité des décors
   * @param type horizontal ou vertical
   * @param decoration
   * @param sideId id du côté s'il existe
   * @param event l'événement de changement
   */
  public changeDecorationQuantity(
    type: 'horizontal' | 'vertical',
    decoration: Decoration,
    sideId: string,
    event: Event
  ): Observable<void> {
    const el = event.target as HTMLInputElement;
    const quantity = parseInt(el.value);

    return of(undefined).pipe(
      tap(() => {
        const configuration = this.configuration();
        if (!configuration) {
          return;
        }

        switch (type) {
          case 'horizontal':
            let indexDecoration = configuration.horizontalDecorations.findIndex(d => d.id === decoration.id);
            let horizontalDecoration = configuration.horizontalDecorations[indexDecoration];
            horizontalDecoration.quantity = quantity;
            this.removeDecorationIfEmpty(configuration.horizontalDecorations, horizontalDecoration);
            break;
          case 'vertical':
            let indexSide = configuration.sides.findIndex(s => s.id === sideId);
            let indexVertDecoration = configuration.sides[indexSide].verticalDecorations.findIndex(
              d => d.id === decoration.id
            );
            let verticalDecoration = configuration.sides[indexSide].verticalDecorations[indexVertDecoration];
            verticalDecoration.quantity = quantity;
            this.removeDecorationIfEmpty(configuration.sides[indexSide].verticalDecorations, verticalDecoration);
            break;
        }

        this.setPanelCount();
        this.updateConfiguration(configuration);
        this.checkPanelError();
      }),
      switchMap(() => from(this.fencePreview.constructPreview()))
    );
  }

  /**
   * Fonction de récupération de la quantité de panneaux
   * @param type horizontal ou vertical
   * @param id id du panneau
   * @param sideId
   * @returns quantité de panneaux
   */
  getPanelQuantity(type: 'horizontal' | 'vertical', id: number, sideId: string): number {
    const configuration = this.configuration();
    if (!configuration) {
      return 0;
    }

    switch (type) {
      case 'horizontal':
        if (configuration.horizontalPanels !== undefined && configuration.horizontalPanels.length > 0) {
          let indexPanel = configuration.horizontalPanels.findIndex(d => d.id === id);
          if (indexPanel >= 0) {
            return configuration.horizontalPanels[indexPanel].quantity !== undefined
              ? configuration.horizontalPanels[indexPanel].quantity
              : 0;
          }
        }
        return 0;
      case 'vertical':
        if (sideId != null) {
          let indexSide = configuration.sides.findIndex(s => s.id === sideId);
          if (
            configuration.sides[indexSide].verticalPanels !== undefined &&
            configuration.sides[indexSide].verticalPanels.length > 0
          ) {
            let indexPanel = configuration.sides[indexSide].verticalPanels.findIndex(d => d.id === id);
            if (indexPanel >= 0) {
              return configuration.sides[indexSide].verticalPanels[indexPanel].quantity !== undefined
                ? configuration.sides[indexSide].verticalPanels[indexPanel].quantity
                : 0;
            }
          }
          return 0;
        }

        //si sideId est null, on fait la somme des quantités de tous les décors verticaux de la configuration pour le id decor donnée
        return configuration.sides.reduce((acc: number, side: Side) => {
          if (side.verticalPanels) {
            return (
              acc +
              side.verticalPanels.reduce((ac: number, panel: Decoration) => {
                if (panel.id === id) {
                  return ac + panel.quantity;
                } else {
                  return ac;
                }
              }, 0)
            );
          } else {
            return acc;
          }
        }, 0);
      default:
        return 0;
    }
  }

  /**
   * Fonction de suppression de tous les panneaux
   */
  deleteAllPanels(): Observable<void> {
    return concat(
      this.resetDecors(this.configuration().horizontalPanels),
      ...this.configuration().sides.map(s => this.resetDecors(s.verticalPanels))
    ).pipe(
      toArray(), // Collecte tous les résultats
      map(() => undefined) // Retourne void
    );
  }

  /**
   * Met à jour la quantité d'un portillon. La fonction le désactive si la quantité est 0
   * @param door le portillon
   * @param event l'événement de changement
   */
  changeDoorQuantity(door: Door, event: Event): Observable<void> {
    const el = event.target as HTMLInputElement;
    door.quantity = parseInt(el.value);

    // si la quantité passe à zéro, on désélectionne la porte
    if (door.quantity == 0) {
      return this.selectDoor(door);
    } else {
      return of(undefined).pipe(
        tap(() => {
          const configuration = this.configuration();
          if (!configuration) {
            return;
          }

          if (configuration.doors) {
            let cd = configuration.doors.find(d => d.id == door.id);
            if (cd) {
              cd.quantity = door.quantity;
            }
          }

          this.setPanelCount();
          this.updateConfiguration(configuration);
          this.checkPanelError();
        }),
        switchMap(() => from(this.fencePreview.constructPreview()))
      );
    }
  }

  /**
   * Change le nombre de lames mixte
   * @param configurationInstallation
   * @param newQuantity
   */
  changeNbLamesMixte(configurationInstallation: ConfigurationInstallation, newQuantity: number): Observable<void> {
    return of(undefined).pipe(
      tap(() => {
        const configuration = this.configuration();

        const configurationBoard = configurationInstallation.configurationBoards.find(b => b.boardNumber);

        configurationBoard.boardNumber = newQuantity;

        this.updateConfiguration(configuration);
      }),
      switchMap(() => from(this.fencePreview.constructPreview()))
    );
  }

  /**
   * change l'ordre des lames
   */
  changeBoardMixOrder(configurationInstallation: ConfigurationInstallation): Observable<void> {
    return of(undefined).pipe(
      tap(() => {
        const configuration = this.configuration();

        let configurationBoards = configurationInstallation.configurationBoards.sort((a, b) => a.order + b.order);

        // Trouver la valeur maximale d'order
        const maxOrder = Math.max(...configurationBoards.map(obj => obj.order));

        configurationBoards = configurationBoards.map(obj => ({
          ...obj,
          order: maxOrder - obj.order,
        }));

        configurationInstallation.configurationBoards = configurationBoards;
        this.updateConfiguration(configuration);
      }),
      switchMap(() => from(this.fencePreview.constructPreview()))
    );
  }

  /**
   * Fonction appelée pour ajouter ou retirer un portillon
   * @param door le portillon
   */
  selectDoor(door: Door): Observable<void> {
    return of(undefined).pipe(
      tap(() => {
        const configuration = this.configuration();
        if (!configuration) {
          return;
        }

        if (!configuration.doors) {
          configuration.doors = [];
        }

        let isDoorAdded = false;
        for (let i = 0; i < configuration.doors.length; i++) {
          if (configuration.doors[i].id === door.id) {
            isDoorAdded = true;
            door.quantity = 0;
            configuration.doors.splice(i, 1);
            break;
          }
        }

        if (!isDoorAdded) {
          door.quantity = 1;
          configuration.doors.unshift(door);
        }

        this.setPanelCount();
        this.updateConfiguration(configuration);
        this.checkPanelError();
      }),
      switchMap(() => from(this.fencePreview.constructPreview()))
    );
  }

  /**
   * Détermine le nombre maximum autorisé pour l'input de quantité du portillon
   * @param door le portillon
   * @returns la quantité max autorisée
   */
  maxDoor(door: Door): number {
    const configuration = this.configuration();

    // Tableau des sections comportant un portillon, trié par la hauteur de la section
    let heightsNeedingDoors = configuration.sides
      .map(s => s.sections.filter(ss => ss.hasDoor))
      .reduce((a, b) => a.concat(b), []) // aplatissement du tableau
      .map(s => ({ height: s.height, doorSelected: false }))
      .sort((s1, s2) => s1.height - s2.height);

    // Valeur max de l'input number = nombre de portillons à sélectionner
    let inputMax = heightsNeedingDoors.length;

    // Si des portillons ont été sélectionnés...
    if (configuration.doors) {
      // Tableau des portillons trié par la hauteur du portillon
      let sortedDoors = configuration.doors
        .map(d => {
          let res = [];
          for (let i = 0; i < d.quantity; i++) {
            res.push({ height: d.height, available: true });
          }
          return res;
        })
        .reduce((a, b) => a.concat(b), []) // aplatissement du tableau
        .sort((d1, d2) => d1.height - d2.height);

      // Affectation des portillons aux sections
      for (let i = 0; i < heightsNeedingDoors.length; i++) {
        let height = heightsNeedingDoors[i];
        if (!height.doorSelected) {
          // Recherche d'un portillon compatible avec la hauteur de la section
          let d = sortedDoors.find(d => d.available && d.height < height.height);
          if (d) {
            // Portillon plus disponible et section affectée
            d.available = false;
            height.doorSelected = true;
          }
        }
      }

      // Compte les sections sans portillon affecté et avec une hauteur compatible avec le portillon actuel
      inputMax = heightsNeedingDoors.filter(h => !h.doorSelected && h.height > door.height).length;
    }

    // Retourne le nombre de portillons restant + la quantité déjà saisie
    return (inputMax > 0 ? inputMax : 0) + (door.quantity ? door.quantity : 0);
  }

  /**
   * Permet de désactiver un portillon si la quantité max est atteinte ou que le portillon est désactivé dans le back
   * @param door le portillon
   * @returns vrai si la quantité max est atteinte
   */
  isDoorDisabled(door: Door): boolean {
    // si le portillon est désactivé en BDD, on le désactive toujours
    if (door.isDisabled) {
      return true;
    }

    // si la quantité max des portillons positionable sur la configuration est atteint, alors on désactive le portillon
    return this.maxDoor(door) === 0;
  }

  /**
   * Fonction de détermination si le portillon est sélectionné ou non
   * @param door  portillon
   * @returns boolean
   */
  isDoorSelected(door: Door): boolean {
    const configuration = this.configuration();
    return !!(configuration.doors && configuration.doors.find((d: Door) => d.id === door.id) != null);
  }

  /**
   * Calcule le nombre maximum d'une décoration possible horizontal dans la configuration
   * @param decoration
   */
  maxHorizontalElement(decoration: Decoration): number {
    const configuration = this.configuration();

    this.setPanelCount();

    let panelCount = configuration.panelCount.completeCount;
    const decorativeBoardsWithHorizontalQuantity =
      configuration.horizontalDecorativeBoards?.filter(d => d.quantity) ?? [];
    const decorationsWithHorizontalQuantity = configuration.horizontalDecorations?.filter(d => d.quantity) ?? [];
    const panelsWithHorizontalQuantity = configuration.horizontalPanels?.filter(d => d.quantity) ?? [];

    const allDecorationQuantities = decorativeBoardsWithHorizontalQuantity
      .concat(decorationsWithHorizontalQuantity)
      .concat(panelsWithHorizontalQuantity)
      .filter(x => x.id !== decoration.id); // on ne prend pas en compte la décoration courante

    for (let i = 0; i < allDecorationQuantities.length; i++) {
      const currentDecoration = allDecorationQuantities[i];
      panelCount -= currentDecoration.quantity;
    }

    return panelCount;
  }

  /**
   * Calcule le nombre maximum d'une décoration possible vertical dans la configuration
   * @param decoration
   * @param side
   */
  maxVerticalElement(decoration: Decoration, side: Side): number {
    const configuration = this.configuration();

    if (!configuration || !decoration || !side) return 0;

    // on récupère l'une des lames (elles font toutes la même longueur dans un mixage)
    const configurationInstallation = configuration.configurationInstallations[0];
    const colorId = configurationInstallation?.configurationBoards[0].boardId;

    if (colorId == null) return 0;

    const color = this.colorStateService.getByInstallationIdAndId(configurationInstallation.installationId, colorId);

    let fenceLength = getSideLengthForDecoration(side, decoration.width);

    const allHorizontalDecorations = [
      ...(configuration.horizontalDecorativeBoards ?? []),
      ...(configuration.horizontalDecorations ?? []),
      ...(configuration.horizontalPanels ?? []),
    ];

    const quantity = allHorizontalDecorations
      .filter(x => x.quantity > 0)
      .map(d => d.quantity)
      .reduce((sum, quantity) => sum + quantity, 0);

    if (quantity) {
      const usedLength = quantity * color.width;

      // On ne peut pas dépasser la taille de la clôture
      if (usedLength > fenceLength) {
        fenceLength = fenceLength % color.width;
      } else {
        fenceLength -= usedLength;
      }
    }

    // Retirer verticaux (lames décoratives, décors et panneaux) autres que celui en cours
    const allVerticalDecorations = [
      ...(side.verticalDecorativeBoards ?? []),
      ...(side.verticalDecorations ?? []),
      ...(side.verticalPanels ?? []),
    ].filter(vd => vd.quantity > 0 && vd.id !== decoration.id);

    const decoQuantityHeightArray = allVerticalDecorations.map(d => ({
      quantity: d.quantity,
      height: d.height,
    }));

    decoQuantityHeightArray.forEach(element => (fenceLength -= element.quantity * element.height));

    // Retirer les portillons sélectionnés
    if (configuration.doors && configuration.doors.length > 0) {
      const doorWidth = configuration.doors[0].width; // approximation: même largeur
      const sectionsWithDoor = side.sections.filter(sec => sec.hasDoor);
      fenceLength -= sectionsWithDoor.length * doorWidth;
    }

    return Math.trunc(fenceLength / decoration.height);
  }

  /**
   * Détermine si la pose a été sélectionné
   * @param installation
   */
  isInstallationSelected(installation: Installation): boolean {
    const configuration = this.configuration();
    return (
      configuration.configurationInstallations.length === 1 &&
      configuration.configurationInstallations.some(ci => ci.installationId === installation.id)
    );
  }

  /**
   * Fonction de détermination si la lame est sélectionnée ou pas
   * @param fenceType lame
   * @returns boolean
   */
  /*isBoardSelected(fenceType: Board): boolean {
    const configuration = this.configuration();

    if (!configuration) {
      return false;
    }

    // Forcément qu'une installation pour l'instant, on ne gère pas le mixage des poses
    return configuration.configurationInstallations[0].configurationBoards.some(cb => cb.fenceTypeId === fenceType.id);
  }*/

  /**
   * Fonction de détermination si la couleur est sélectionnée ou non
   * @param configurationInstallation
   * @param board couleur
   * @returns boolean
   */
  isColorSelected(configurationInstallation: ConfigurationInstallation, board: Color): boolean {
    const configuration = this.configuration();

    if (!configuration) {
      return false;
    }

    // si c'est une lame mix de choisi dans la configuration
    if (configurationInstallation.configurationBoards.length > 1) {
      // la lame à vérifier n'est pas un mix
      if (!board.primaryId || !board.secondaryId) {
        return false;
      }

      const configurationColorIds = [board.primaryId, board.secondaryId];
      return configurationInstallation.configurationBoards.every(b => configurationColorIds.includes(b.boardId));
    }

    // si c'est une lame simple
    // Forcément qu'une installation pour l'instant, on ne gère pas le mixage des poses
    return configurationInstallation.configurationBoards.some(cb => cb.boardId === board.id);
  }

  /**
   * Fonction de détermination si le poteau est sélectionné ou non
   * @param postType type de poteau
   * @returns boolean
   */
  isPostTypeSelected(postType: PostType): boolean {
    const configuration = this.configuration();
    return configuration.postType !== undefined && configuration.postType.id === postType.id;
  }

  /**
   * Fonction de détermination si le champ est coché ou non
   * @param type horizontal ou vertical
   * @param decorativeBoard lame décorative
   * @returns boolean
   */
  isDecorativeBoardChecked(type: 'horizontal' | 'vertical', decorativeBoard: Decoration): boolean {
    const configuration = this.configuration();

    switch (type) {
      case 'horizontal':
        if (configuration.horizontalDecorativeBoards !== undefined) {
          if (configuration.horizontalDecorativeBoards.some(d => d.id === decorativeBoard.id && d.quantity > 0)) {
            return true;
          }
        }
        return false;
      case 'vertical':
        return configuration.sides.some(s => s.verticalDecorativeBoards.some(d => d.id === decorativeBoard.id));
      default:
        return false;
    }
  }

  /**
   * Fonction de détermination si le champ est coché ou non
   * @param type horizontal ou vertical
   * @param decor décor
   * @returns boolean
   */
  isDecorationChecked(type: 'horizontal' | 'vertical', decor: Decoration): boolean {
    const configuration = this.configuration();

    switch (type) {
      case 'horizontal':
        if (configuration.horizontalDecorations !== undefined) {
          if (configuration.horizontalDecorations.some(d => d.id === decor.id && d.quantity > 0)) {
            return true;
          }
        }
        return false;
      case 'vertical':
        return configuration.sides.some(s => s.verticalDecorations.some(d => d.id === decor.id));
      default:
        return false;
    }
  }

  /**
   * Fonction pour vérifier que les décorations ont été complétées
   * @param decorations
   * @returns boolean
   */
  private isDecorationsCompleted(decorations: Decoration[]): boolean {
    let result = true;

    if (decorations != null) {
      for (let i = 0; i < decorations.length; i++) {
        if (decorations[i].quantity == null || (decorations[i].quantity && decorations[i].isDisabled)) {
          result = false;

          if (!result) {
            break;
          }
        }
      }
    }

    return result;
  }

  /**
   * Fonction de récupération de la quantité de lames décoratives
   * @param type horizontal ou vertical
   * @param decorativeBoard décor
   * @param side côté s'il existe
   * @returns quantité de lames décoratives
   */
  getDecorativeBoardQuantity(type: 'horizontal' | 'vertical', decorativeBoard: Decoration, side: Side | null): number {
    const configuration = this.configuration();

    switch (type) {
      case 'horizontal':
        if (
          configuration.horizontalDecorativeBoards !== undefined &&
          configuration.horizontalDecorativeBoards.length > 0
        ) {
          let indexDecorativeBoard = configuration.horizontalDecorativeBoards.findIndex(
            d => d.id === decorativeBoard.id
          );
          if (indexDecorativeBoard >= 0) {
            return configuration.horizontalDecorativeBoards[indexDecorativeBoard].quantity !== undefined
              ? configuration.horizontalDecorativeBoards[indexDecorativeBoard].quantity
              : 0;
          }
        }
        return 0;
      case 'vertical':
        if (side != null && side.id != null) {
          let indexSide = configuration.sides.findIndex(s => s.id === side.id);
          if (
            configuration.sides[indexSide].verticalDecorativeBoards !== undefined &&
            configuration.sides[indexSide].verticalDecorativeBoards.length > 0
          ) {
            let indexDecorativeBoard = configuration.sides[indexSide].verticalDecorativeBoards.findIndex(
              d => d.id === decorativeBoard.id
            );
            if (indexDecorativeBoard >= 0) {
              return configuration.sides[indexSide].verticalDecorativeBoards[indexDecorativeBoard].quantity !==
                undefined
                ? configuration.sides[indexSide].verticalDecorativeBoards[indexDecorativeBoard].quantity
                : 0;
            }
          }
          return 0;
        }

        //si sideId est null, on fait la somme des quantités de tous les décors verticaux de la configuration pour l'id decor donnée
        return configuration.sides.reduce((acc: number, side: Side) => {
          if (side.verticalDecorativeBoards) {
            return (
              acc +
              side.verticalDecorativeBoards.reduce((ac: number, decoration: Decoration) => {
                if (decoration.id === decorativeBoard.id) {
                  return ac + decoration.quantity;
                } else {
                  return ac;
                }
              }, 0)
            );
          } else {
            return acc;
          }
        }, 0);
      default:
        return 0;
    }
  }

  /**
   * Fonction de récupération de la quantité de décors
   * @param type horizontal ou vertical
   * @param decor décor
   * @param side côté s'il existe
   * @returns quantité de décors
   */
  getDecorationQuantity(type: 'horizontal' | 'vertical', decor: Decoration, side: Side | null): number {
    const configuration = this.configuration();

    switch (type) {
      case 'horizontal':
        if (configuration.horizontalDecorations !== undefined && configuration.horizontalDecorations.length > 0) {
          let indexDecoration = configuration.horizontalDecorations.findIndex(d => d.id === decor.id);
          if (indexDecoration >= 0) {
            return configuration.horizontalDecorations[indexDecoration].quantity !== undefined
              ? configuration.horizontalDecorations[indexDecoration].quantity
              : 0;
          }
        }
        return 0;
      case 'vertical':
        if (side != null && side.id != null) {
          let indexSide = configuration.sides.findIndex(s => s.id === side.id);
          if (
            configuration.sides[indexSide].verticalDecorations !== undefined &&
            configuration.sides[indexSide].verticalDecorations.length > 0
          ) {
            let indexDecoration = configuration.sides[indexSide].verticalDecorations.findIndex(d => d.id === decor.id);
            if (indexDecoration >= 0) {
              return configuration.sides[indexSide].verticalDecorations[indexDecoration].quantity !== undefined
                ? configuration.sides[indexSide].verticalDecorations[indexDecoration].quantity
                : 0;
            }
          }
          return 0;
        }

        //si sideId est null, on fait la somme des quantités de tous les décors verticaux de la configuration pour l'id decor donnée
        return configuration.sides.reduce((acc: number, side: Side) => {
          if (side.verticalDecorations) {
            return (
              acc +
              side.verticalDecorations.reduce((ac: number, decoration: Decoration) => {
                if (decoration.id === decor.id) {
                  return ac + decoration.quantity;
                } else {
                  return ac;
                }
              }, 0)
            );
          } else {
            return acc;
          }
        }, 0);
      default:
        return 0;
    }
  }

  selectInstallationMixing(installationMixing: InstallationMixing): Observable<void> {
    return of(undefined).pipe(
      switchMap(() => {
        const configuration = this.configuration();

        if (
          configuration.configurationInstallations.some(
            i => i.installationId === installationMixing.primaryInstallation.id
          ) &&
          configuration.configurationInstallations.some(
            i => i.installationId === installationMixing.secondaryInstallation.id
          )
        ) {
          return of(undefined);
        }

        // on reset les portillons, panneaux, décorations et lames décoratives sélectionnés lorsqu'on change de pose
        this.resetConfigurationDecorativeBoards();
        this.resetConfigurationDecorations();
        this.resetConfigurationPanels();
        this.resetConfigurationDoors();

        configuration.isVertical = false;
        configuration.configurationInstallations = [
          new ConfigurationInstallation(installationMixing.secondaryInstallation.id, 1, []),
          new ConfigurationInstallation(installationMixing.primaryInstallation.id, 0, [], 1),
        ];

        this.updateConfiguration(configuration);

        // recharge les lames disponibles
        return this.boardStateService.setList(configuration).pipe(
          switchMap(() => {
            const primaryBoards = this.boardStateService.getBoardByInstallation(
              installationMixing.primaryInstallation.id
            );
            const secondaryBoards = this.boardStateService.getBoardByInstallation(
              installationMixing.secondaryInstallation.id
            );

            const primaryConfigurationInstallation = configuration.configurationInstallations.find(
              ci => ci.installationId === installationMixing.primaryInstallation.id
            );
            const secondaryConfigurationInstallation = configuration.configurationInstallations.find(
              ci => ci.installationId === installationMixing.secondaryInstallation.id
            );

            return forkJoin([
              this.selectBoard(primaryConfigurationInstallation, primaryBoards[0]),
              this.selectBoard(secondaryConfigurationInstallation, secondaryBoards[0]),
            ]).pipe(
              switchMap(() => {
                // recalcule le nombre de lames à afficher par défaut
                primaryConfigurationInstallation.boardNumber = this.getDefaultInstallationMixingQuantity(
                  installationMixing,
                  primaryBoards[0]
                );

                this.updateConfiguration(configuration);
                return from(this.fencePreview.constructPreview());
              })
            );
          })
        );
      })
    );
  }

  isInstallationMixingSelected(installationMixing: InstallationMixing): boolean {
    const configuration = this.configuration();
    if (!configuration) {
      return false;
    }

    return (
      configuration.configurationInstallations.some(
        i => i.installationId === installationMixing.primaryInstallation.id
      ) &&
      configuration.configurationInstallations.some(
        i => i.installationId === installationMixing.secondaryInstallation.id
      )
    );
  }

  /**
   * Fonction de check si le nombre de décors est inférieur au nombre de panneaux maximum
   */
  private checkPanelError(): void {
    const configuration = this.configuration();
    if (!configuration) {
      return;
    }

    if (!this.checkPanelCount()) {
      let message = '';
      this.translateService.get('fence.materials.error-panel-count').subscribe(text => (message = text));
      // Ouverture de la modale d'erreur si le nombre de décors est supérieur au nombre de panneaux maximum
      this.modalService.display(MODAL.GENERIC_ERROR, { errorMessage: message });
    }
  }

  private resetDecors(decors: Decoration[]): Observable<void> {
    return of(undefined).pipe(
      tap(() => {
        if (!decors) {
          return;
        }

        decors.splice(0, decors.length);
        const configuration = this.configuration();
        this.setPanelCount();
        this.updateConfiguration(configuration);
      }),
      switchMap(() => from(this.fencePreview.constructPreview()))
    );
  }

  private updateConfiguration(configuration: Configuration): void {
    configuration = { ...configuration } as Configuration;
    this.configuration.set(configuration);
    this.fenceService.updateConfiguration(configuration);
  }

  /**
   * Recalcule le nombre de panneaux de lame disponible
   * @private
   */
  private setPanelCount(): void {
    setPanelCount(this.configuration());
  }

  /**
   * Check si le nombre de décors n'est pas supérieur au nombre de panneaux complets
   * @private
   */
  private checkPanelCount(): boolean {
    return checkPanelCount(this.configuration());
  }

  /**
   * Reset les lames décoratives sélectionnés
   * @private
   */
  private resetConfigurationDecorativeBoards(): void {
    const configuration = this.configuration();
    configuration.horizontalDecorativeBoards = [];

    for (const side of configuration.sides) {
      side.verticalDecorativeBoards = [];
    }

    this.updateConfiguration(configuration);
  }

  /**
   * Reset les décorations sélectionnés
   * @private
   */
  private resetConfigurationDecorations(): void {
    const configuration = this.configuration();
    configuration.horizontalDecorations = [];

    for (const side of configuration.sides) {
      side.verticalDecorations = [];
    }

    this.updateConfiguration(configuration);
  }

  /**
   * Reset les panneaux sélectionnés
   * @private
   */
  private resetConfigurationPanels(): void {
    const configuration = this.configuration();
    configuration.horizontalPanels = [];

    for (const side of configuration.sides) {
      side.verticalPanels = [];
    }

    this.updateConfiguration(configuration);
  }

  /**
   * Reset les portillons sélectionnés
   * @private
   */
  private resetConfigurationDoors(): void {
    const configuration = this.configuration();

    configuration.doors = [];

    this.updateConfiguration(configuration);
  }

  changeSecondaryInstallationQuantity(newQuantity: number): Observable<void> {
    return of(undefined).pipe(
      tap(() => {
        const configuration = this.configuration();

        const secondaryConfigurationInstallation = configuration.configurationInstallations.find(ci => ci.boardNumber);
        secondaryConfigurationInstallation.boardNumber = newQuantity;

        this.updateConfiguration(configuration);
      }),
      switchMap(() => from(this.fencePreview.constructPreview()))
    );
  }

  /**
   * change l'ordre des poses
   */
  changeInstallationMixOrder(): Observable<void> {
    return of(undefined).pipe(
      tap(() => {
        const configuration = this.configuration();

        let configurationInstallations = configuration.configurationInstallations.sort((a, b) => a.order + b.order);

        // Trouver la valeur maximale d'order
        const maxOrder = Math.max(...configurationInstallations.map(obj => obj.order));

        configurationInstallations = configurationInstallations.map(obj => ({
          ...obj,
          order: maxOrder - obj.order,
        }));

        configuration.configurationInstallations = configurationInstallations;
        this.updateConfiguration(configuration);
      }),
      switchMap(() => from(this.fencePreview.constructPreview()))
    );
  }

  /**
   * Calcule le nombre de lame par défaut à afficher pour une pose mixte
   * Par faut on remplie 2/3 de la hauteur
   * @param installationMixing
   * @param board
   * @private
   */
  private getDefaultInstallationMixingQuantity(installationMixing: InstallationMixing, board: Board): number {
    const maxHeight = getMaxHeight(this.configuration());

    // prend 2/3 de la hauteur disponible
    const defaultHeight = (maxHeight * 2) / 3;

    const configurationInstallation: ConfigurationInstallation = new ConfigurationInstallation(
      installationMixing.primaryInstallation.id,
      0,
      [new ConfigurationBoard(0, board.id, board.colors[0])]
    );

    // calcul une configuration avec 2/3 de la hauteur disponible pour la pose secondaire
    const FencePanel = this.fencePanelCalculatorService.calculate(defaultHeight, undefined, [
      configurationInstallation,
    ]);

    return (
      FencePanel.installationColors.find(ic => ic.installation.id === installationMixing.primaryInstallation.id)?.colors
        .length ?? 0
    );
  }
}
